import React, { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import Helmet from 'react-helmet'
import { Location, LocationDescriptor } from 'history'
import queryString from 'query-string'

// Contants
import {
  RouteProps,
  routes,
  ProfessionalRole,
  professionalRoles,
} from '@sketch/constants'

// Components
import CampaignSpecificSignUpView from './CampaignSpecificSignUpView'
import { DocumentHead } from '@sketch/components'
import RoleSpecificSignUpView from './RoleSpecificSignUpView'
import SketchLoginDeprecationModal from '../../modals/SketchLoginDeprecationModal'
import { useModalContext } from 'containers/ModalManager/ModalContext'

// Utils
import { useQueryParams } from 'utils'
import { trackEvent } from 'modules/analytics'
import { useGoogleAnalytics } from 'modules/analytics/google-analytics'

// GQL
import {
  useAcceptWorkspaceInviteMutation,
  useAcceptProjectMembershipInvitationMutation,
} from '@sketch/gql-types'

// Types
interface LocationState {
  from: Location
}
interface SignUpViewProps extends RouteProps<'SIGN_UP'> {
  location: Location<LocationState>
}
interface ProjectInviteProps {
  from: LocationDescriptor
  projectInvitation: {
    projectMembershipIdentifier: string
    token: string
  }
}

const SignUpView: React.FC<SignUpViewProps> = ({ history, location }) => {
  const { trackGAEvent } = useGoogleAnalytics()
  const { pathname } = useLocation()

  useEffect(() => {
    trackGAEvent('event', 'page_view', {
      page_path: pathname,
    })
  }, [trackGAEvent, pathname])

  const { state } = location
  const { showModal } = useModalContext()

  const queryParams = useQueryParams<'SIGN_UP'>()
  const maybeCampaign = queryParams['utm_campaign']

  const linkState = { from: state?.from }
  const searchQuery: {
    token?: string
    email?: string
  } = queryString.parse(linkState?.from?.search)

  const [acceptWorkspaceInvite] = useAcceptWorkspaceInviteMutation({
    // As a temporary solution we call the mutation and proceed without
    // handling any possible errors.
    // https://github.com/sketch-hq/Cloud/issues/10236
    onError: 'do-nothing',
  })

  const [acceptProjectInvite] = useAcceptProjectMembershipInvitationMutation({
    // As a temporary solution we call the mutation and proceed without
    // handling any possible errors.
    // https://github.com/sketch-hq/Cloud/issues/10236
    onError: 'do-nothing',
  })

  // Object.keys(...) loses type information so without using `as
  // ProfessionalRole[]` typescript will assume it returns `string[]`.
  const roles = Object.keys(professionalRoles) as ProfessionalRole[]
  const professionalRole = roles.find(
    role => role === queryParams.professionalRole
  )

  const email = decodeURIComponent(queryParams?.email ?? '')

  const onCompleted = () => {
    if (queryParams.sketch === 'true') {
      showModal(SketchLoginDeprecationModal)
    }

    // Check if the user has a workspace invitation to accept
    if (
      linkState.from?.pathname === routes.WORKSPACE_INVITE.template() &&
      linkState.from?.search
    ) {
      if (searchQuery.token) {
        acceptWorkspaceInvite({
          variables: {
            membershipToken: searchQuery.token,
          },
        })
      }
    }

    const projectInvitationState = state as ProjectInviteProps
    if (
      projectInvitationState?.projectInvitation?.projectMembershipIdentifier &&
      projectInvitationState?.projectInvitation?.token
    ) {
      const { projectInvitation } = projectInvitationState
      acceptProjectInvite({
        variables: {
          input: {
            token: projectInvitation.token,
            projectMembershipIdentifier:
              projectInvitation.projectMembershipIdentifier,
          },
        },
      })
    }

    trackEvent('SIGN UP - Sign up form success', {
      formContext: 'main',
    })

    history.push(routes.CHECK_YOUR_INBOX.create({}))
  }

  const signUpProps = {
    email: email.length ? email : searchQuery.email,
    invitedUser: Boolean(searchQuery.token),
    professionalRole,
    onCompleted,
  }

  return (
    <>
      <Helmet>
        <link rel="canonical" href="https://www.sketch.com/signup/" />
        <meta property="og:url" content="https://www.sketch.com/signup/" />
      </Helmet>
      <DocumentHead title="Sign up: Your best work starts here" />
      {maybeCampaign ? (
        <CampaignSpecificSignUpView campaign={maybeCampaign} {...signUpProps} />
      ) : (
        <RoleSpecificSignUpView {...signUpProps} />
      )}
    </>
  )
}

export default SignUpView
